import { useRef, useState } from "react";

// MUI imports
import { Alert, Card, Chip, Divider, Grid, Radio, RadioGroup, FormControlLabel, MenuItem, Select } from "@mui/material";

// MUI Icons imports
import Icon from "@mui/material/Icon";
import PersonIcon from "@mui/icons-material/Person";

// Customized MUI components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Other imports
import PropTypes from "prop-types";
import {
    calculateAddonAmount,
    calculateAccommodationAmount,
    getOfferWindow,
    resolveHss,
    computeHssSurcharge,
} from "./hardcodedData";
import {
    daysUntilDeparture,
    resolveApplicableInstallments,
} from "utils/installmentSchedule";
import { formatAmount } from "./TravelOrderUtility";
import { getCancellationTypeLabel } from "utils/paymentTermConstants";

const GATEWAY_OPTIONS = [
    { id: "stripe", label: "Stripe (Card)" },
    { id: "razorpay", label: "Razorpay" },
];

const formatDate = (date) =>
    date ? new Date(date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "";

const formatPaymentType = (type) => {
    switch (type) {
        case "full_payment": return "Full Payment";
        case "deposit_final_balance": return "Deposit + Final Balance";
        case "deposit_installments": return "Deposit + Installments";
        default: return type;
    }
};

const PAYMENT_TYPE_FILTERS = [
    { value: "all", label: "All types" },
    { value: "full_payment", label: "Full Payment" },
    { value: "deposit_final_balance", label: "Deposit + Final Balance" },
    { value: "deposit_installments", label: "Deposit + Installments" },
];

// Square-ish radio indicators (4px radius) for the payment plan list
const radioBoxSx = {
    width: 18,
    height: 18,
    borderRadius: "4px",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
};

const SquareRadioIcon = <MDBox sx={{ ...radioBoxSx, border: "2px solid #B0BAC9" }} />;
const SquareRadioCheckedIcon = (
    <MDBox sx={{ ...radioBoxSx, border: "2px solid #1565C8" }}>
        <MDBox sx={{ width: 8, height: 8, borderRadius: "2px", bgcolor: "#1565C8" }} />
    </MDBox>
);

/* -------------------------------------------------------------------------- */
/*  SectionCard — same look as InfoCard on the travel order detail view        */
/* -------------------------------------------------------------------------- */

function SectionCard({ icon, title, subtitle, headerAction, children }) {
    return (
        <Card sx={{ borderRadius: "12px", border: "1px solid #E8EDF5", boxShadow: "none", height: "100%", overflow: "hidden" }}>
            <MDBox
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                px={2}
                py={1.25}
                sx={{ bgcolor: "#F4F6F9", borderBottom: "1px solid #E8EDF5" }}
            >
                <MDBox display="flex" alignItems="center" gap={1}>
                    <Icon sx={{ fontSize: 20, color: "#5C6B82" }}>{icon}</Icon>
                    <MDBox>
                        <MDTypography variant="button" fontWeight="bold" color="dark" display="block" lineHeight={1.2}>
                            {title}
                        </MDTypography>
                        {subtitle && (
                            <MDTypography variant="caption" color="text" display="block">
                                {subtitle}
                            </MDTypography>
                        )}
                    </MDBox>
                </MDBox>
                {headerAction}
            </MDBox>
            <MDBox p={2.5}>{children}</MDBox>
        </Card>
    );
}

SectionCard.propTypes = {
    icon: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    headerAction: PropTypes.node,
    children: PropTypes.node,
};

function EditButton({ onClick }) {
    if (!onClick) return null;
    return (
        <MDButton variant="text" color="info" size="small" onClick={onClick}>
            Edit
        </MDButton>
    );
}

EditButton.propTypes = { onClick: PropTypes.func };

/* -------------------------------------------------------------------------- */
/*  Small pieces used inside section 1                                         */
/* -------------------------------------------------------------------------- */

function SubPanel({ title, icon, onEdit, children }) {
    return (
        <MDBox sx={{ border: "1px solid #E8EDF5", borderRadius: "12px", height: "100%", overflow: "hidden" }}>
            <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                px={1.5}
                py={0.5}
                sx={{ bgcolor: "#FAFBFD", borderBottom: "1px solid #E8EDF5" }}
            >
                <MDBox display="flex" alignItems="center" gap={0.75}>
                    <Icon sx={{ fontSize: 17, color: "#5C6B82" }}>{icon}</Icon>
                    <MDTypography variant="caption" fontWeight="bold" color="dark">
                        {title}
                    </MDTypography>
                </MDBox>
                <EditButton onClick={onEdit} />
            </MDBox>
            <MDBox px={1.5} py={1.25} display="flex" flexDirection="column" gap={0.5}>
                {children}
            </MDBox>
        </MDBox>
    );
}

SubPanel.propTypes = {
    title: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    onEdit: PropTypes.func.isRequired,
    children: PropTypes.node,
};

function EmptyHint({ children }) {
    return (
        <MDTypography variant="caption" color="text" sx={{ fontStyle: "italic", opacity: 0.65 }}>
            {children}
        </MDTypography>
    );
}

EmptyHint.propTypes = { children: PropTypes.node };

function ItemRow({ label, count }) {
    return (
        <MDBox display="flex" justifyContent="space-between" alignItems="center" gap={1}>
            <MDTypography variant="caption" color="text" sx={{ flex: 1 }}>
                {label}
            </MDTypography>
            {count > 0 && (
                <MDBox display="flex" alignItems="center" gap={0.25} sx={{ flexShrink: 0 }}>
                    <PersonIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                    <MDTypography variant="caption" color="text" fontWeight="medium">
                        {count}
                    </MDTypography>
                </MDBox>
            )}
        </MDBox>
    );
}

ItemRow.propTypes = { label: PropTypes.node, count: PropTypes.number };

function SummaryRow({ label, value, bold }) {
    return (
        <MDBox display="flex" justifyContent="space-between" alignItems="center" py={0.5}>
            <MDTypography variant={bold ? "button" : "caption"} color={bold ? "dark" : "text"} fontWeight={bold ? "bold" : "regular"}>
                {label}
            </MDTypography>
            <MDTypography variant="button" fontWeight={bold ? "bold" : "medium"} color="dark">
                {value}
            </MDTypography>
        </MDBox>
    );
}

SummaryRow.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    bold: PropTypes.bool,
};

/* -------------------------------------------------------------------------- */
/*  Cancellation policy helpers                                                */
/* -------------------------------------------------------------------------- */

const POLICY_TYPE_META = {
    customer: { icon: "person", hint: "If the traveler cancels" },
    organization: { icon: "apartment", hint: "If the organization cancels" },
    non_payment: { icon: "money_off", hint: "If payment isn't completed on time" },
    other: { icon: "policy", hint: "" },
};
const POLICY_TYPE_ORDER = ["customer", "organization", "non_payment", "other"];

// Green for generous refunds, amber for partial, red for little or none
const getRefundTone = (pct) => {
    const value = Number(pct) || 0;
    if (value >= 75) return { fg: "#1B7F4B", bg: "#E6F4EC" };
    if (value >= 25) return { fg: "#A15C00", bg: "#FFF3E0" };
    return { fg: "#B3261E", bg: "#FDECEA" };
};

// Rules are sorted high -> low, so each tier covers [its days, previous tier's days - 1]
const formatDayRange = (days, prevDays) => {
    const d = Number(days) || 0;
    if (prevDays == null) return `${d}+ days before departure`;
    if (d === 0) return `Less than ${prevDays} days before departure`;
    return `${d}–${prevDays - 1} days before departure`;
};

const formatRefundMethod = (rule) => {
    if (!rule.refund_method) return null;
    if (rule.refund_method === "Coupon" && rule.coupon_expiry_days) {
        return `Coupon, valid for ${rule.coupon_expiry_days} days`;
    }
    return rule.refund_method;
};

function CancellationPolicies({ policies, isLoading }) {
    if (!isLoading && !policies?.length) return null;

    const groups = (policies || []).reduce((acc, rule) => {
        const key = POLICY_TYPE_META[rule.cancellation_type] ? rule.cancellation_type : "other";
        (acc[key] = acc[key] || []).push(rule);
        return acc;
    }, {});
    const orderedKeys = POLICY_TYPE_ORDER.filter((key) => groups[key]);

    return (
        <SectionCard
            icon="event_busy"
            title="Cancellation & Refund Policy"
            subtitle="What the customer gets back, depending on when and why the booking is cancelled"
        >
            {isLoading ? (
                <MDTypography variant="caption" color="text">
                    Loading cancellation policy…
                </MDTypography>
            ) : (
                <>
                    <Grid container spacing={3}>
                        {orderedKeys.map((key) => {
                            const rules = [...groups[key]].sort((a, b) => (b.days ?? 0) - (a.days ?? 0));
                            return (
                                <Grid item xs={12} md={orderedKeys.length === 1 ? 12 : orderedKeys.length === 2 ? 6 : 4} key={key}>
                                    <MDTypography variant="caption" fontWeight="bold" color="dark" display="block" mb={0.5}>
                                        {key === "other" ? "Other cancellations" : getCancellationTypeLabel(key)}
                                    </MDTypography>

                                    {rules.map((rule) => {
                                        const tone = getRefundTone(rule.amount_percent);
                                        const method = formatRefundMethod(rule);
                                        const prevDays = rules[rules.indexOf(rule) - 1]?.days;
                                        return (
                                            <MDBox
                                                key={rule.id}
                                                display="flex"
                                                justifyContent="space-between"
                                                alignItems="center"
                                                gap={1}
                                                py={1.25}
                                                sx={{ borderTop: "1px solid #EFF2F6" }}
                                            >
                                                <MDBox>
                                                    <MDTypography variant="caption" fontWeight="medium" color="dark" display="block">
                                                        {formatDayRange(rule.days, prevDays)}
                                                    </MDTypography>
                                                    {method && (
                                                        <MDTypography variant="caption" color="text" display="block" sx={{ fontSize: "0.7rem" }}>
                                                            {method}
                                                        </MDTypography>
                                                    )}
                                                </MDBox>
                                                <Chip
                                                    size="small"
                                                    label={`${Number(rule.amount_percent) || 0}% refund`}
                                                    sx={{ bgcolor: tone.bg, color: tone.fg, fontWeight: 700, fontSize: "0.7rem", flexShrink: 0 }}
                                                />
                                            </MDBox>
                                        );
                                    })}
                                </Grid>
                            );
                        })}
                    </Grid>

                    <MDBox display="flex" alignItems="center" gap={0.5} mt={1.5}>
                        <Icon sx={{ fontSize: 14, color: "text.secondary" }}>info</Icon>
                        <MDTypography variant="caption" color="text" sx={{ fontSize: "0.7rem" }}>
                            Refund percentages apply to the amount already paid at the time of cancellation.
                        </MDTypography>
                    </MDBox>
                </>
            )}
        </SectionCard>
    );
}

CancellationPolicies.propTypes = {
    policies: PropTypes.arrayOf(PropTypes.object),
    isLoading: PropTypes.bool,
};

/* -------------------------------------------------------------------------- */
/*  Main step                                                                  */
/* -------------------------------------------------------------------------- */

function ReviewPaymentStep({
                               selectedTripPackage,
                               selectedDepartureDate,
                               paymentError,
                               onEditStep,
                               flightPreference,
                               travelers,
                               selectedAccommodations,
                               accommodationTravelerMap,
                               selectedAddons,
                               addonTravelerMap,
                               isLoadingTemplates,
                               templatesError,
                               paymentTemplates,
                               selectedPaymentTemplateId,
                               onSelectPaymentTemplate,
                               gatewayAvailability,
                               isLoadingGateways,
                               gatewayError,
                               selectedGateway,
                               onGatewayChange,
                               pricing,
                               bookingStatus,
                               isProcessingPayment,
                               onPayNow,
                               paymentTermPolicies,
                               isLoadingPolicies,
                               isSupplementMode = false,
                               supplement = null,
                               readOnly = false,
                           }) {
    const isPlanLocked = Boolean(isSupplementMode || readOnly);
    const [paymentTypeFilter, setPaymentTypeFilter] = useState("all");
    const [showTemplateWarning, setShowTemplateWarning] = useState(false);
    const paymentPlanRef = useRef(null);

    if (!selectedTripPackage) return null;

    const allTemplates = paymentTemplates || [];

    const selectedTemplate = allTemplates.find(
        (t) => String(t.id) === String(selectedPaymentTemplateId)
    );

    const filteredTemplates = allTemplates
        .filter(
            (t) =>
                paymentTypeFilter === "all" ||
                t.payment_type === paymentTypeFilter
        )
        .filter(
            (t) => String(t.id) !== String(selectedPaymentTemplateId)
        );

// Keep the selected template at the top, even if it doesn't match the filter.
    const displayedTemplates = selectedTemplate
        ? [selectedTemplate, ...filteredTemplates]
        : filteredTemplates;

    const templateMissing = !selectedPaymentTemplateId;

    const handleSendPaymentLink = () => {
        if (readOnly) return;
        if (templateMissing) {
            setShowTemplateWarning(true);
            paymentPlanRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            return;
        }
        onPayNow();
    };

    const noGatewayConnected = !gatewayAvailability?.stripe && !gatewayAvailability?.razorpay;

    return (
        <MDBox>
            {/* Header */}

            <MDTypography variant="h6" fontWeight="bold" mb={0.5}>
                Review Your Booking
            </MDTypography>
            <MDTypography variant="body2" color="text" mb={2}>
                Please review all details before proceeding to payment.
            </MDTypography>

            {paymentError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {paymentError}
                </Alert>
            )}

            <Grid container spacing={3}>

                {/* Section 1 : Booking Details (trip, travelers, accommodation, add-ons) */}

                <Grid item xs={12}>
                    <SectionCard icon="confirmation_number" title="Booking Details">
                        {/* Trip */}
                        <MDBox display="flex" gap={2} alignItems="center" flexWrap="wrap" mb={2.5}>
                            <MDBox
                                component="img"
                                src={selectedTripPackage.image}
                                alt={selectedTripPackage.title}
                                sx={{ width: 88, height: 64, borderRadius: 1.5, objectFit: "cover", bgcolor: "#F0F3F8", flexShrink: 0 }}
                            />
                            <MDBox flex={1} minWidth={200}>
                                <MDTypography variant="button" fontWeight="bold" color="dark" display="block">
                                    {selectedTripPackage.title}
                                </MDTypography>
                                <MDTypography variant="caption" color="text" display="block">
                                    {new Date(selectedDepartureDate?.departure_date).toLocaleDateString("en-IN")} -{" "}
                                    {new Date(selectedDepartureDate?.return_date).toLocaleDateString("en-IN")}
                                    {selectedTripPackage.duration ? `  |  ${selectedTripPackage.duration}` : ""}
                                </MDTypography>
                                <MDTypography variant="caption" color="text" display="block">
                                    Booking Category: {flightPreference}
                                </MDTypography>
                            </MDBox>
                            <MDBox display="flex" alignItems="center" gap={1}>
                                <MDBox textAlign="right">
                                    <MDTypography variant="caption" color="text" display="block">
                                        Travelers
                                    </MDTypography>
                                    <MDTypography variant="button" fontWeight="bold" color="dark">
                                        {travelers?.length}
                                    </MDTypography>
                                </MDBox>
                                <EditButton onClick={readOnly || !onEditStep ? null : () => onEditStep(0)} />
                            </MDBox>
                        </MDBox>

                        <Divider sx={{ my: 0, mb: 2.5 }} />

                        {/* Travelers / Accommodation / Add-ons */}
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <SubPanel title="Travelers" icon="groups" onEdit={readOnly || !onEditStep ? null : () => onEditStep(1)}>
                                    {travelers.map((traveler, index) => (
                                        <MDTypography key={traveler.id} variant="caption" color="text" display="block">
                                            {index + 1}. {traveler.first_name || "Unnamed traveler"}
                                            {traveler.isPrimary ? " (Primary)" : ""}
                                        </MDTypography>
                                    ))}
                                </SubPanel>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <SubPanel title="Accommodation" icon="maps_home_work" onEdit={readOnly || !onEditStep ? null : () => onEditStep(2)}>
                                    {selectedAccommodations.length ? (
                                        selectedAccommodations.map((item) => (
                                            <ItemRow
                                                key={item.addon.id}
                                                label={`• ${item.linkedService.name}`}
                                                count={(accommodationTravelerMap[item.addon.id] || []).length}
                                            />
                                        ))
                                    ) : (
                                        <EmptyHint>No accommodation selected</EmptyHint>
                                    )}
                                </SubPanel>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <SubPanel title="Add-ons" icon="celebration" onEdit={readOnly || !onEditStep ? null : () => onEditStep(2)}>
                                    {selectedAddons.length ? (
                                        selectedAddons.map((item) => (
                                            <ItemRow
                                                key={item.addon.id}
                                                label={`• ${item.addon.name}`}
                                                count={(addonTravelerMap[item.addon.id] || []).length}
                                            />
                                        ))
                                    ) : (
                                        <EmptyHint>No add-ons selected</EmptyHint>
                                    )}
                                </SubPanel>
                            </Grid>
                        </Grid>
                    </SectionCard>
                </Grid>

                {/* Section 2 : Payment Plan */}

                <Grid item xs={12} ref={paymentPlanRef} sx={{ scrollMarginTop: "90px" }}>
                    <SectionCard
                        icon="event_repeat"
                        title="Payment Plan"
                        subtitle={isPlanLocked ? "Payment plan cannot be changed because a payment link has already been generated" : "Select how you'd like to pay for this trip"}
                        headerAction={
                            !isLoadingTemplates && paymentTemplates.length > 0 ? (
                                <Select
                                    size="small"
                                    disabled={isPlanLocked}
                                    value={paymentTypeFilter}
                                    onChange={(e) => setPaymentTypeFilter(e.target.value)}
                                    inputProps={{ "aria-label": "Filter payment plans by type" }}
                                    sx={{
                                        minWidth: 200,
                                        height: 34,
                                        bgcolor: "#fff",
                                        borderRadius: "8px",
                                        cursor: isPlanLocked ? "not-allowed" : "pointer",
                                        "& .MuiSelect-select": { py: 0.5, display: "flex", alignItems: "center" },
                                    }}
                                >
                                    {PAYMENT_TYPE_FILTERS.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            ) : null
                        }
                    >
                        {showTemplateWarning && templateMissing && (
                            <Alert
                                severity="warning"
                                sx={{
                                    mb: 2,
                                    py: 0,
                                    px: 1.25,
                                    alignItems: "center",
                                    fontSize: "0.75rem",
                                    fontWeight: 700,
                                    borderRadius: "8px",
                                    "& .MuiAlert-icon": { fontSize: 16, py: 0.75, mr: 0.75 },
                                    "& .MuiAlert-message": { py: 0.75 },
                                }}
                            >
                                Please select a payment plan to send the payment link to the customer.
                            </Alert>
                        )}

                        {isLoadingTemplates && (
                            <MDTypography variant="caption" color="text">Loading payment plans…</MDTypography>
                        )}
                        {!isLoadingTemplates && templatesError && (
                            <Alert severity="error">{templatesError}</Alert>
                        )}
                        {!isLoadingTemplates && !templatesError && paymentTemplates.length === 0 && (
                            <MDTypography variant="caption" color="text">
                                No payment plans are configured for this trip.
                            </MDTypography>
                        )}

                        {!isLoadingTemplates && paymentTemplates.length > 0 && (
                            <MDBox
                                display="flex"
                                flexDirection="column"
                                gap={1.25}
                                sx={{ maxHeight: 340, overflowY: "auto", pr: 0.5 }}
                            >
                                {displayedTemplates.length === 0 && (
                                    <MDTypography variant="caption" color="text" sx={{ fontStyle: "italic", opacity: 0.7 }}>
                                        No payment plans of this type for this trip.
                                    </MDTypography>
                                )}
                                {displayedTemplates.map((template) => {
                                    const isSelected = selectedPaymentTemplateId === template.id;
                                    const isInstallments = template.payment_type === "deposit_installments";
                                    const daysLeft = selectedDepartureDate?.departure_date
                                        ? daysUntilDeparture(new Date(), selectedDepartureDate.departure_date)
                                        : null;
                                    const applicable = isInstallments
                                        ? resolveApplicableInstallments(template.installments, daysLeft)
                                        : [];
                                    const totalInstallments =
                                        template.number_of_installments || (template.installments || []).length;

                                    return (
                                        <MDBox
                                            key={template.id}
                                            onClick={isPlanLocked ? undefined : () => onSelectPaymentTemplate(template.id)}
                                            sx={{
                                                border: isSelected ? "2px solid #1565C8" : "1px solid #E8EDF5",
                                                bgcolor: isPlanLocked
                                                    ? (isSelected ? "#F0F6FD" : "#F8FAFC")
                                                    : (isSelected ? "#F3F8FE" : "transparent"),
                                                opacity: isPlanLocked
                                                    ? (isSelected ? 1 : 0.6)
                                                    : 1,
                                                borderRadius: "12px",
                                                display: "flex",
                                                gap: 1.5,
                                                p: 1.5,
                                                cursor: isPlanLocked ? "not-allowed" : "pointer",
                                                transition: "background-color 0.15s ease, border-color 0.15s ease",
                                                "&:hover": isPlanLocked
                                                    ? {}
                                                    : { bgcolor: isSelected ? "#F3F8FE" : "#FAFBFD", borderColor: "#1565C8" },
                                            }}
                                        >
                                            <Radio
                                                size="small"
                                                disabled={isPlanLocked}
                                                checked={isSelected}
                                                icon={SquareRadioIcon}
                                                checkedIcon={SquareRadioCheckedIcon}
                                                disableRipple
                                                sx={{ alignSelf: "flex-start", p: 0.25, cursor: isPlanLocked ? "not-allowed" : "pointer" }}
                                            />

                                            <MDBox flex={1} minWidth={0}>
                                                <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                                                    <MDTypography fontWeight="medium" variant="body2" color="dark">
                                                        {template.template_name}
                                                    </MDTypography>
                                                    <MDBox display="flex" alignItems="center" gap={0.75}>
                                                        {isPlanLocked && isSelected && (
                                                            <Chip
                                                                size="small"
                                                                icon={<Icon sx={{ fontSize: "14px !important", color: "#475467 !important" }}>lock</Icon>}
                                                                label="Locked"
                                                                sx={{ bgcolor: "#E2E8F0", color: "#334155", fontWeight: 700, fontSize: "0.65rem", height: 20 }}
                                                            />
                                                        )}
                                                        <Chip
                                                            size="small"
                                                            label={formatPaymentType(template.payment_type)}
                                                            sx={{ bgcolor: "#E3F2FD", color: "#1565C8", fontWeight: 700, fontSize: "0.65rem", height: 20 }}
                                                        />
                                                    </MDBox>
                                                </MDBox>

                                                {template.payment_type === "full_payment" && (
                                                    <MDTypography variant="caption" color="text" display="block">
                                                        100% due at booking
                                                    </MDTypography>
                                                )}

                                                {template.payment_type === "deposit_final_balance" && (
                                                    <MDTypography variant="caption" color="text" display="block">
                                                        {template.deposit_percentage}% deposit now, balance due{" "}
                                                        {template.due_days_before_departure} days before departure
                                                    </MDTypography>
                                                )}

                                                {isInstallments && (
                                                    <>
                                                        <MDTypography variant="caption" color="text" display="block">
                                                            {template.deposit_percentage}% deposit now
                                                            {selectedDepartureDate?.departure_date &&
                                                                `, then ${applicable.length} of ${totalInstallments} installments apply`}
                                                        </MDTypography>
                                                        {applicable.length > 0 && (
                                                            <MDBox mt={0.75} display="flex" flexDirection="column" gap={0.25}>
                                                                {applicable.map((inst) => (
                                                                    <MDTypography
                                                                        key={inst.installment_number}
                                                                        variant="caption"
                                                                        color="text"
                                                                        display="block"
                                                                    >
                                                                        Installment {inst.installment_number}: {inst.installment_percentage}%, due{" "}
                                                                        {inst.days_before_departure} days before departure
                                                                    </MDTypography>
                                                                ))}
                                                            </MDBox>
                                                        )}
                                                    </>
                                                )}
                                            </MDBox>
                                        </MDBox>
                                    );
                                })}
                            </MDBox>
                        )}

                    </SectionCard>
                </Grid>

                {/* Section 3 : Cancellation & Refund Policy */}

                <Grid item xs={12}>
                    <CancellationPolicies policies={paymentTermPolicies} isLoading={isLoadingPolicies} />
                </Grid>

                {/* Section 4 : Payment Gateway */}

                <Grid item xs={12} md={6}>
                    <SectionCard
                        icon="account_balance"
                        title="Payment Gateway"
                        subtitle="Select Stripe or Razorpay for this payment"
                    >
                        {isLoadingGateways && (
                            <MDTypography variant="caption" color="text">Loading payment gateways…</MDTypography>
                        )}
                        {gatewayError && (
                            <Alert severity="error" sx={{ mb: 1 }}>{gatewayError}</Alert>
                        )}
                        {!isLoadingGateways && !gatewayError && (
                            <>
                                {noGatewayConnected && (
                                    <Alert severity="warning">
                                        No payment gateways are connected. Ask a system admin to set up Stripe or Razorpay under Settings → Payments.
                                    </Alert>
                                )}
                                <RadioGroup
                                    value={selectedGateway || ""}
                                    onChange={(e) => onGatewayChange(e.target.value)}
                                    name="payment-gateway"
                                >
                                    {GATEWAY_OPTIONS.filter((opt) => {
                                        if (opt.id === "stripe") return gatewayAvailability?.stripe;
                                        if (opt.id === "razorpay") return gatewayAvailability?.razorpay;
                                        return false;
                                    }).map((gateway) => (
                                        <FormControlLabel
                                            key={gateway.id}
                                            value={gateway.id}
                                            disabled={readOnly}
                                            control={<Radio size="small" disabled={readOnly} />}
                                            label={<MDTypography variant="button">{gateway.label}</MDTypography>}
                                            sx={{ mb: 0.25 }}
                                        />
                                    ))}
                                </RadioGroup>
                            </>
                        )}
                    </SectionCard>
                </Grid>

                {/* Section 5 : Payment Summary */}

                <Grid item xs={12} md={6}>
                    <SectionCard
                        icon="receipt_long"
                        title="Payment Summary"
                        subtitle="Total amount and how it will be collected"
                    >
                        <SummaryRow
                            label="Total Amount"
                            value={formatAmount(pricing.total, selectedTripPackage.currency)}
                            bold
                        />

                        {pricing.paymentSchedule.length > 0 && <Divider sx={{ my: 1 }} />}

                        {pricing.paymentSchedule.map((row, i) => (
                            <SummaryRow
                                key={i}
                                label={row.dueDate ? `${row.label} (${formatDate(row.dueDate)})` : row.label}
                                value={formatAmount(row.amount, selectedTripPackage.currency)}
                            />
                        ))}

                        <Divider sx={{ my: 1.5 }} />

                        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <MDTypography variant="caption" color="text">
                                Booking Status
                            </MDTypography>
                            <Chip
                                size="small"
                                label={bookingStatus}
                                sx={{ bgcolor: "#FFF3E0", color: "#E68A06", fontWeight: 700, fontSize: "0.7rem" }}
                            />
                        </MDBox>
                        {(!isSupplementMode || !supplement?.lines?.length) && (
                            <MDButton
                                variant="gradient"
                                color={readOnly ? "secondary" : "info"}
                                fullWidth
                                disabled={
                                    readOnly
                                    || !selectedGateway
                                    || isProcessingPayment
                                    || isLoadingGateways
                                    || noGatewayConnected
                                }
                                onClick={readOnly ? undefined : handleSendPaymentLink}
                            >
                                {readOnly
                                    ? `Order is ${bookingStatus} (Read Only)`
                                    : (isProcessingPayment
                                        ? "Preparing payment link..."
                                        : (isSupplementMode ? "Resend payment link to customer" : "Send payment link to customer"))}
                            </MDButton>
                        )}
                    </SectionCard>
                </Grid>
                {isSupplementMode && supplement?.lines?.length > 0 && (() => {
                    const currency = selectedTripPackage.currency;
                    return (
                        <Grid item xs={12}>
                            <Card
                                sx={{
                                    borderRadius: "12px",
                                    border: "2px solid #F0C36D",
                                    boxShadow: "none",
                                    overflow: "hidden",
                                }}
                            >
                                {/* Header strip */}
                                <MDBox
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    px={2}
                                    py={1.25}
                                    sx={{ bgcolor: "#F4F6F9", borderBottom: "1px solid #E8EDF5" }}
                                >
                                    <MDBox display="flex" alignItems="center" gap={1}>
                                        <Icon sx={{ fontSize: 22, color: "#A15C00" }}>add_shopping_cart</Icon>
                                        <MDBox>
                                            <MDTypography variant="button" fontWeight="bold" color="dark" display="block" lineHeight={1.2}>
                                                New Add-ons
                                            </MDTypography>
                                            <MDTypography variant="caption" color="text" display="block">
                                                Not part of the original booking. Billed separately with its own invoice and payment link.
                                            </MDTypography>
                                        </MDBox>
                                    </MDBox>
                                    <Chip
                                        size="small"
                                        label="Additional payment"
                                        sx={{ bgcolor: "#FFE29A", color: "#7A4A00", fontWeight: 700, fontSize: "0.7rem" }}
                                    />
                                </MDBox>

                                <MDBox p={2.5}>
                                    <Grid container spacing={3} alignItems="flex-start">
                                        {/* Items + cost */}
                                        <Grid item xs={12} md={7}>
                                            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                                <MDTypography variant="caption" fontWeight="bold" color="dark">
                                                    Selected add-ons
                                                </MDTypography>
                                                <EditButton onClick={readOnly || !onEditStep ? null : () => onEditStep(2)} />
                                            </MDBox>

                                            {supplement.lines.map((l) => (
                                                <MDBox key={l.key} display="flex" justifyContent="space-between" alignItems="flex-start" py={0.5}>
                                                    <MDBox>
                                                        <MDTypography variant="caption" color="text" display="block">{l.label}</MDTypography>
                                                        {l.detail && (
                                                            <MDTypography variant="caption" color="text" display="block" sx={{ opacity: 0.7, fontSize: "0.7rem" }}>
                                                                {l.detail}
                                                            </MDTypography>
                                                        )}
                                                    </MDBox>
                                                    <MDTypography variant="button" fontWeight="medium" color="dark">
                                                        {formatAmount(l.amount, currency)}
                                                    </MDTypography>
                                                </MDBox>
                                            ))}

                                            <Divider sx={{ my: 1 }} />

                                            {/* Subtotal: bolder and bigger */}
                                            <MDBox display="flex" justifyContent="space-between" alignItems="center" py={0.5}>
                                                <MDTypography variant="button" fontWeight="bold" color="dark" sx={{ fontSize: "0.95rem" }}>
                                                    Subtotal
                                                </MDTypography>
                                                <MDTypography variant="button" fontWeight="bold" color="dark" sx={{ fontSize: "0.95rem" }}>
                                                    {formatAmount(supplement.subtotal, currency)}
                                                </MDTypography>
                                            </MDBox>

                                            {supplement.taxLines.map((t) => (
                                                <SummaryRow key={t.key} label={t.label} value={formatAmount(t.amount, currency)} />
                                            ))}

                                            <Divider sx={{ my: 1 }} />

                                            {/* Total: same weight and size as Subtotal */}
                                            <MDBox display="flex" justifyContent="space-between" alignItems="center" py={0.5}>
                                                <MDTypography variant="button" fontWeight="bold" color="dark" sx={{ fontSize: "0.95rem" }}>
                                                    Total
                                                </MDTypography>
                                                <MDTypography variant="button" fontWeight="bold" color="dark" sx={{ fontSize: "0.95rem" }}>
                                                    {formatAmount(supplement.total, currency)}
                                                </MDTypography>
                                            </MDBox>

                                        </Grid>

                                        {/* Amount due + CTA */}
                                        <Grid item xs={12} md={5}>
                                            <MDBox
                                                p={2}
                                                borderRadius="12px"
                                                sx={{ bgcolor: "#fff", border: "1px solid #E8EDF5" }}
                                            >
                                                <MDTypography variant="caption" color="text" display="block">
                                                    Amount due now
                                                </MDTypography>
                                                <MDTypography variant="h4" fontWeight="bold" color="dark" mb={0.5}>
                                                    {formatAmount(supplement.total, currency)}
                                                </MDTypography>
                                                <MDTypography variant="caption" color="text" display="block" mb={2} sx={{ opacity: 0.8 }}>
                                                    Paid in a single payment. Your existing installment schedule is not affected.
                                                </MDTypography>

                                                <MDButton
                                                    variant="gradient"
                                                    color={readOnly ? "secondary" : "warning"}
                                                    fullWidth
                                                    disabled={
                                                        readOnly
                                                        || !selectedGateway
                                                        || isProcessingPayment
                                                        || isLoadingGateways
                                                        || noGatewayConnected
                                                    }
                                                    onClick={readOnly ? undefined : onPayNow}
                                                >
                                                    {readOnly
                                                        ? `Order is ${bookingStatus} (Read Only)`
                                                        : (isProcessingPayment
                                                            ? "Preparing payment link..."
                                                            : "Send payment link for add-ons")}
                                                </MDButton>

                                                {!selectedGateway && !noGatewayConnected && (
                                                    <MDTypography variant="caption" color="error" display="block" mt={1}>
                                                        Select a payment gateway above to continue.
                                                    </MDTypography>
                                                )}
                                            </MDBox>
                                        </Grid>
                                    </Grid>
                                </MDBox>
                            </Card>
                        </Grid>
                    );
                })()}
            </Grid>
        </MDBox>
    );
}

export const buildReviewPricing = ({
                                       baseSubtotal,
                                       selectedAccommodations,
                                       accommodationTravelerMap,
                                       selectedAddons,
                                       addonTravelerMap,
                                       tripNights,
                                       paymentTemplate,
                                       selectedDepartureDate,
                                       bookingDate = new Date(),
                                       hssTemplate = null,
                                       taxes,
                                       travelerCount = 1,
                                   }) => {

    // sum each selected room, priced by its own assigned traveler count
    const accommodationAmount = selectedAccommodations.reduce((sum, accItem) => {
        const roomTravelerCount = (accommodationTravelerMap[accItem.addon.id] || []).length;
        return sum + calculateAccommodationAmount(accItem, tripNights, roomTravelerCount);
    }, 0);

    // sum each selected addon, priced by its own assigned traveler count
    const addonAmount = selectedAddons.reduce((sum, item) => {
        const addonTravelerCount = (addonTravelerMap[item.addon.id] || []).length;
        return sum + calculateAddonAmount(item.addon, addonTravelerCount);
    }, 0);

    // Total price : [ base - discount + HSS surcharge + addons + accommodation + taxes ]
    const rawDiscountPercentage = selectedDepartureDate?.discount_percentage || 0;
    const offerWindow = getOfferWindow(selectedDepartureDate);
    const isDiscountLive = rawDiscountPercentage > 0 && (!offerWindow || offerWindow.isActive);
    const discountPercentage = isDiscountLive ? rawDiscountPercentage : 0;
    const discountedPrice = baseSubtotal - (baseSubtotal * discountPercentage) / 100;

    const hss = resolveHss(selectedDepartureDate, hssTemplate);
    const isHssLive = Boolean(hss?.isActive);
    const hssSurchargeAmount = computeHssSurcharge(hss, travelerCount); // hss_amount × pax, 0 if not live
    const priceAfterHss = discountedPrice + hssSurchargeAmount;

    const subtotal = priceAfterHss + accommodationAmount + addonAmount;

    // Taxes - configured from Settings > Company Settings > Taxes
    let total_tax_amount = 0;
    for (const tax of taxes || []) {
        total_tax_amount += subtotal * (tax.rate / 100);
    }

    const round2 = (n) => Math.round(n * 100) / 100;
    const total = round2(subtotal + total_tax_amount);

    // no template selected yet : nothing is payable until the user picks one
    let amountPayable = 0;
    let remainingAmount = total;
    let paymentSchedule = [];

    const dueDateFromDays = (days) => {
        if (!selectedDepartureDate?.departure_date || days == null) return null;
        const d = new Date(selectedDepartureDate?.departure_date);
        d.setDate(d.getDate() - days);
        return d;
    };

    if (paymentTemplate) {
        const depositPct = parseFloat(paymentTemplate.deposit_percentage) || 0;

        if (paymentTemplate.payment_type === "full_payment") {
            amountPayable = total;
            remainingAmount = 0;
            paymentSchedule = [{ label: "Full Payment", amount: total, dueDate: null }];

        } else if (paymentTemplate.payment_type === "deposit_final_balance") {
            amountPayable = (total * depositPct) / 100;
            remainingAmount = total - amountPayable;
            paymentSchedule = [
                { label: "Deposit (Today)", amount: amountPayable, dueDate: null },
                {
                    label: "Final Balance",
                    amount: remainingAmount,
                    dueDate: dueDateFromDays(paymentTemplate.due_days_before_departure),
                },
            ];

        } else if (paymentTemplate.payment_type === "deposit_installments") {
            amountPayable = Math.round((total * depositPct) / 100);
            const daysRemaining = daysUntilDeparture(
                bookingDate,
                selectedDepartureDate?.departure_date
            );
            const applicableInstallments = resolveApplicableInstallments(
                paymentTemplate.installments,
                daysRemaining
            );
            let pendingBalance = total - amountPayable;
            const installmentRows = applicableInstallments.map((inst, idx) => {
                const pct = parseFloat(inst.installment_percentage) || 0;
                let amount;
                if (idx === applicableInstallments.length - 1) {
                    amount = pendingBalance;
                } else {
                    amount = Math.round((total * pct) / 100);
                    if (amount > pendingBalance) amount = pendingBalance;
                }
                pendingBalance = Math.max(0, pendingBalance - amount);
                return {
                    label: `Installment ${inst.installment_number}`,
                    amount,
                    dueDate: dueDateFromDays(inst.days_before_departure),
                };
            });
            remainingAmount = installmentRows.reduce((s, r) => s + r.amount, 0);
            paymentSchedule = [
                { label: "Deposit (Today)", amount: amountPayable, dueDate: null },
                ...installmentRows,
            ];
        }
    }

    return {
        subtotal,
        accommodationAmount,
        addonAmount,
        hssSurchargeAmount,
        isHssLive,
        total,
        amountPayable,
        remainingAmount,
        paymentSchedule,
    };
};

ReviewPaymentStep.propTypes = {
    readOnly: PropTypes.bool,
    selectedTripPackage: PropTypes.object,
    selectedDepartureDate: PropTypes.object,
    paymentError: PropTypes.string,
    onEditStep: PropTypes.func.isRequired,
    flightPreference: PropTypes.string,
    travelers: PropTypes.arrayOf(PropTypes.object).isRequired,
    selectedAccommodations: PropTypes.arrayOf(PropTypes.object).isRequired,
    accommodationTravelerMap: PropTypes.object.isRequired,
    selectedAddons: PropTypes.arrayOf(PropTypes.object).isRequired,
    addonTravelerMap: PropTypes.object.isRequired,
    isLoadingTemplates: PropTypes.bool,
    templatesError: PropTypes.string,
    paymentTemplates: PropTypes.arrayOf(PropTypes.object),
    invalidPaymentTemplate: PropTypes.bool,
    selectedPaymentTemplateId: PropTypes.number,
    onSelectPaymentTemplate: PropTypes.func.isRequired,
    gatewayAvailability: PropTypes.shape({
        stripe: PropTypes.bool,
        razorpay: PropTypes.bool,
        currency: PropTypes.string,
    }),
    isLoadingGateways: PropTypes.bool,
    gatewayError: PropTypes.string,
    selectedGateway: PropTypes.string,
    onGatewayChange: PropTypes.func.isRequired,
    pricing: PropTypes.shape({
        total: PropTypes.number,
        amountPayable: PropTypes.number,
        remainingAmount: PropTypes.number,
        paymentSchedule: PropTypes.array,
    }).isRequired,
    bookingStatus: PropTypes.string.isRequired,
    isProcessingPayment: PropTypes.bool.isRequired,
    onPayNow: PropTypes.func.isRequired,
    paymentTermPolicies: PropTypes.arrayOf(PropTypes.object),
    isLoadingPolicies: PropTypes.bool,
};

export default ReviewPaymentStep;